import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Bell,
  CheckCheck,
  Eraser,
  Info,
  Package,
  Trash2,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useDeleteAllReadNotifications,
  useDeleteNotification,
  useMarkAllRead,
} from '@/hooks/useNotifications';
import { getNotifications } from '@/services/api.client';
import type { AppNotification, NotificationCategory } from '@/types';
import { formatRelative } from '@/lib/utils';

const FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'visitor_check_in', label: 'Visitors' },
  { value: 'delivery_arrival', label: 'Deliveries' },
  { value: 'walk_in_pending', label: 'Walk-ins' },
  { value: 'system', label: 'System' },
];

const CATEGORY_ICON: Record<NotificationCategory, React.ElementType> = {
  visitor_check_in: Users,
  delivery_arrival: Package,
  walk_in_pending: UserPlus,
  walk_in_resolved: UserCheck,
  domestic_worker_no_show: Bell,
  system: Info,
};

const CATEGORY_COLOUR: Record<NotificationCategory, string> = {
  visitor_check_in: 'text-[var(--status-green-text)]',
  delivery_arrival: 'text-[var(--status-blue-text)]',
  walk_in_pending: 'text-[var(--status-amber-text)]',
  walk_in_resolved: 'text-[var(--status-green-text)]',
  domestic_worker_no_show: 'text-destructive',
  system: 'text-muted-foreground',
};

function NotificationRow({ notif }: { notif: AppNotification }) {
  const deleteMut = useDeleteNotification();
  const Icon = CATEGORY_ICON[notif.category] ?? Bell;
  const colour = CATEGORY_COLOUR[notif.category] ?? 'text-muted-foreground';

  const content = (
    <div className="flex items-start gap-3 py-3 group">
      {/* Unread dot */}
      <div className="mt-1.5 shrink-0">
        {notif.isRead ? (
          <div className="size-2" />
        ) : (
          <div className="size-2 rounded-full bg-primary" />
        )}
      </div>
      {/* Icon */}
      <div
        className={`size-8 rounded-full bg-muted flex items-center justify-center shrink-0 ${colour}`}
      >
        <Icon className="size-4" />
      </div>
      {/* Text */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium ${notif.isRead ? 'text-muted-foreground' : 'text-foreground'}`}
        >
          {notif.title}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.body}</p>
        <p className="text-xs text-muted-foreground/60 mt-1">{formatRelative(notif.createdAt)}</p>
      </div>
      {/* Delete */}
      <Button
        variant="ghost"
        size="icon"
        className="size-7 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0 text-muted-foreground hover:text-destructive"
        onClick={(e) => {
          e.preventDefault();
          deleteMut.mutate(notif.id);
        }}
        disabled={deleteMut.isPending}
      >
        <Trash2 className="size-3.5" />
      </Button>
    </div>
  );

  // Walk-in pending → link to approval page
  if (notif.category === 'walk_in_pending' && notif.actionId) {
    return (
      <Link
        to={`/resident/walk-in/${notif.actionId}`}
        className="block hover:bg-muted/40 rounded-lg px-2 -mx-2 transition-colors border-b border-border/50 last:border-0"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="border-b border-border/50 last:border-0 hover:bg-muted/40 rounded-lg px-2 -mx-2 transition-colors cursor-default">
      {content}
    </div>
  );
}

export function NotificationsPage() {
  const [filter, setFilter] = useState('');
  const markAllRead = useMarkAllRead();
  const deleteAllRead = useDeleteAllReadNotifications();

  const params =
    filter === 'unread'
      ? { isRead: false as const }
      : filter
        ? { category: filter as NotificationCategory }
        : undefined;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useInfiniteQuery({
      queryKey: ['notifications-page', filter],
      queryFn: ({ pageParam = 1 }) =>
        getNotifications({ ...params, page: pageParam as number, pageSize: 20 }),
      getNextPageParam: (last) =>
        last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined,
      initialPageParam: 1,
      retry: false,
    });

  const allNotifs = data?.pages.flatMap((p) => p.data) ?? [];
  const totalUnread = data?.pages[0]?.meta.total ?? 0;

  if (isError) {
    return (
      <div className="space-y-4 max-w-2xl">
        <h1 className="text-2xl">Notifications</h1>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Bell className="size-10 mb-3 text-muted-foreground/30" />
          <p className="text-sm font-medium text-muted-foreground">Couldn't load notifications</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Something went wrong. Please refresh and try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl">Notifications</h1>
          {totalUnread > 0 && (
            <p className="text-sm text-muted-foreground mt-1">{totalUnread} unread</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending || totalUnread === 0}
          >
            <CheckCheck className="size-4 mr-2" />
            Mark all read
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => deleteAllRead.mutate()}
            disabled={deleteAllRead.isPending}
          >
            <Eraser className="size-4 mr-2" />
            Clear read
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex-wrap h-auto gap-1">
          {FILTERS.map((f) => (
            <TabsTrigger key={f.value} value={f.value} className="text-xs">
              {f.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="pt-4">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="size-8 rounded-full mt-1" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-64" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : allNotifs.length ? (
            <>
              <div>
                {allNotifs.map((n) => (
                  <NotificationRow key={n.id} notif={n} />
                ))}
              </div>
              {hasNextPage && (
                <div className="pt-4 text-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="py-10 text-center">
              <Bell className="size-10 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No notifications yet.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Visitor arrivals, deliveries, and walk-in requests will appear here.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
